import { createContext, useContext, useCallback, ReactNode } from "react";
import { supabase } from "@/lib/supabase";

// Points system rules:
// - 1 point per 10 SAR spent (rounded down)
// - Tiers: Bronze (0-99), Silver (100-299), Gold (300-599), Platinum (600+)
// - Points are added when order status changes to "confirmed" or "delivered"

export interface RewardsData {
  points: number;
  totalPointsEarned: number;
  tier: "bronze" | "silver" | "gold" | "platinum";
  tierLabel: string;
  tierColor: string;
  tierBg: string;
  tierBorder: string;
  tierIcon: string;
  nextTierPoints: number | null;
  nextTierLabel: string | null;
  progressPercent: number;
}

export interface RedeemOption {
  points: number;
  discountPercent: number;
  discountValue: number;
  label: string;
  description: string;
}

export interface RedeemedCoupon {
  id: string;
  couponCode: string;
  pointsUsed: number;
  discountValue: number;
  discountPercent: number;
  status: "active" | "used" | "expired";
  expiresAt: string;
  createdAt: string;
  usedAt: string | null;
}

interface RewardsContextType {
  getRewards: (customerId: string) => Promise<RewardsData | null>;
  addPoints: (customerId: string, orderId: string, orderTotal: number) => Promise<number>;
  getPointsHistory: (customerId: string) => Promise<PointsHistoryItem[]>;
  redeemPoints: (customerId: string, option: RedeemOption) => Promise<{ success: boolean; coupon?: RedeemedCoupon; error?: string }>;
  getRedeemedCoupons: (customerId: string) => Promise<RedeemedCoupon[]>;
  markCouponUsed: (couponCode: string) => Promise<boolean>;
}

// Redeem options: points → discount
export const REDEEM_OPTIONS: RedeemOption[] = [
  { points: 50,  discountPercent: 5,  discountValue: 0, label: "خصم 5%",  description: "50 نقطة مقابل خصم 5% على طلبك" },
  { points: 100, discountPercent: 10, discountValue: 0, label: "خصم 10%", description: "100 نقطة مقابل خصم 10% على طلبك" },
  { points: 200, discountPercent: 15, discountValue: 0, label: "خصم 15%", description: "200 نقطة مقابل خصم 15% على طلبك" },
  { points: 300, discountPercent: 20, discountValue: 0, label: "خصم 20%", description: "300 نقطة مقابل خصم 20% على طلبك" },
];

export interface PointsHistoryItem {
  orderRef: string;
  date: string;
  pointsEarned: number;
  orderTotal: number;
  status: string;
}

const TIERS = [
  { key: "bronze",   label: "برونزي",   min: 0,   max: 99,  color: "text-amber-700",   bg: "bg-amber-700/10",   border: "border-amber-700/30",   icon: "ri-medal-line" },
  { key: "silver",   label: "فضي",      min: 100, max: 299, color: "text-gray-300",    bg: "bg-gray-300/10",    border: "border-gray-300/30",    icon: "ri-medal-2-line" },
  { key: "gold",     label: "ذهبي",     min: 300, max: 599, color: "text-amber-400",   bg: "bg-amber-400/10",   border: "border-amber-400/30",   icon: "ri-vip-crown-line" },
  { key: "platinum", label: "بلاتيني",  min: 600, max: Infinity, color: "text-cyan-300", bg: "bg-cyan-300/10", border: "border-cyan-300/30",   icon: "ri-vip-diamond-line" },
] as const;

function calcTier(points: number): RewardsData["tier"] {
  if (points >= 600) return "platinum";
  if (points >= 300) return "gold";
  if (points >= 100) return "silver";
  return "bronze";
}

function calcPointsFromTotal(total: number): number {
  return Math.floor(total / 10);
}

export function buildRewardsData(points: number, totalPointsEarned: number): RewardsData {
  const tierKey = calcTier(points);
  const tierIdx = TIERS.findIndex((t) => t.key === tierKey);
  const tier = TIERS[tierIdx];
  const nextTier = TIERS[tierIdx + 1] ?? null;

  const rangeMin = tier.min;
  const rangeMax = tier.max === Infinity ? points : tier.max;
  const progressPercent = tier.max === Infinity
    ? 100
    : Math.min(100, Math.round(((points - rangeMin) / (rangeMax - rangeMin + 1)) * 100));

  return {
    points,
    totalPointsEarned,
    tier: tierKey,
    tierLabel: tier.label,
    tierColor: tier.color,
    tierBg: tier.bg,
    tierBorder: tier.border,
    tierIcon: tier.icon,
    nextTierPoints: nextTier ? nextTier.min - points : null,
    nextTierLabel: nextTier ? nextTier.label : null,
    progressPercent,
  };
}

const RewardsContext = createContext<RewardsContextType | null>(null);

export function RewardsProvider({ children }: { children: ReactNode }) {
  const getRewards = useCallback(async (customerId: string): Promise<RewardsData | null> => {
    try {
      const { data, error } = await supabase
        .from("customers")
        .select("points, total_points_earned")
        .eq("id", customerId)
        .maybeSingle();
      if (error || !data) return null;
      return buildRewardsData(data.points ?? 0, data.total_points_earned ?? 0);
    } catch {
      return null;
    }
  }, []);

  const addPoints = useCallback(async (
    customerId: string,
    orderId: string,
    orderTotal: number
  ): Promise<number> => {
    try {
      const earned = calcPointsFromTotal(orderTotal);
      if (earned <= 0) return 0;

      // Check if points already added for this order
      const { data: orderRow } = await supabase
        .from("orders")
        .select("points_earned, id")
        .eq("id", orderId)
        .maybeSingle();

      if (orderRow?.points_earned && orderRow.points_earned > 0) return 0; // already added

      // Get current points
      const { data: customer } = await supabase
        .from("customers")
        .select("points, total_points_earned")
        .eq("id", customerId)
        .maybeSingle();

      const currentPoints = customer?.points ?? 0;
      const currentTotal = customer?.total_points_earned ?? 0;

      // Update customer points
      await supabase
        .from("customers")
        .update({
          points: currentPoints + earned,
          total_points_earned: currentTotal + earned,
        })
        .eq("id", customerId);

      // Mark order as points earned
      await supabase
        .from("orders")
        .update({ points_earned: earned })
        .eq("id", orderId);

      return earned;
    } catch {
      return 0;
    }
  }, []);

  const getPointsHistory = useCallback(async (customerId: string): Promise<PointsHistoryItem[]> => {
    try {
      const { data } = await supabase
        .from("orders")
        .select("order_ref, created_at, points_earned, final_price, status")
        .eq("customer_id", customerId)
        .gt("points_earned", 0)
        .order("created_at", { ascending: false });

      return (data ?? []).map((row) => ({
        orderRef: String(row.order_ref),
        date: String(row.created_at),
        pointsEarned: Number(row.points_earned),
        orderTotal: Number(row.final_price),
        status: String(row.status),
      }));
    } catch {
      return [];
    }
  }, []);

  const redeemPoints = useCallback(async (
    customerId: string,
    option: RedeemOption
  ): Promise<{ success: boolean; coupon?: RedeemedCoupon; error?: string }> => {
    try {
      // Check current points
      const { data: customer } = await supabase
        .from("customers")
        .select("points")
        .eq("id", customerId)
        .maybeSingle();

      const currentPoints = customer?.points ?? 0;
      if (currentPoints < option.points) {
        return { success: false, error: "نقاطك غير كافية لهذا الاستبدال" };
      }

      // Generate unique coupon code
      const code = `POINTS-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).substring(2, 6).toUpperCase()}`;

      // Expires in 30 days
      const expiresAt = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();

      // Insert coupon
      const { data: couponRow, error: insertErr } = await supabase
        .from("redeem_coupons")
        .insert({
          customer_id: customerId,
          coupon_code: code,
          points_used: option.points,
          discount_value: option.discountValue,
          discount_percent: option.discountPercent,
          status: "active",
          expires_at: expiresAt,
        })
        .select()
        .maybeSingle();

      if (insertErr || !couponRow) {
        return { success: false, error: "حدث خطأ أثناء إنشاء الكوبون" };
      }

      // Deduct points from customer
      await supabase
        .from("customers")
        .update({ points: currentPoints - option.points })
        .eq("id", customerId);

      const coupon: RedeemedCoupon = {
        id: couponRow.id,
        couponCode: couponRow.coupon_code,
        pointsUsed: couponRow.points_used,
        discountValue: couponRow.discount_value,
        discountPercent: couponRow.discount_percent,
        status: couponRow.status,
        expiresAt: couponRow.expires_at,
        createdAt: couponRow.created_at,
        usedAt: couponRow.used_at ?? null,
      };

      return { success: true, coupon };
    } catch {
      return { success: false, error: "حدث خطأ غير متوقع" };
    }
  }, []);

  const getRedeemedCoupons = useCallback(async (customerId: string): Promise<RedeemedCoupon[]> => {
    try {
      const { data } = await supabase
        .from("redeem_coupons")
        .select("*")
        .eq("customer_id", customerId)
        .order("created_at", { ascending: false });

      return (data ?? []).map((row) => ({
        id: row.id,
        couponCode: row.coupon_code,
        pointsUsed: row.points_used,
        discountValue: row.discount_value,
        discountPercent: row.discount_percent,
        status: row.status as RedeemedCoupon["status"],
        expiresAt: row.expires_at,
        createdAt: row.created_at,
        usedAt: row.used_at ?? null,
      }));
    } catch {
      return [];
    }
  }, []);

  const markCouponUsed = useCallback(async (couponCode: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from("redeem_coupons")
        .update({ status: "used", used_at: new Date().toISOString() })
        .eq("coupon_code", couponCode)
        .eq("status", "active");
      return !error;
    } catch {
      return false;
    }
  }, []);

  return (
    <RewardsContext.Provider value={{ getRewards, addPoints, getPointsHistory, redeemPoints, getRedeemedCoupons, markCouponUsed }}>
      {children}
    </RewardsContext.Provider>
  );
}

export function useRewards() {
  const ctx = useContext(RewardsContext);
  if (!ctx) throw new Error("useRewards must be used within RewardsProvider");
  return ctx;
}
